import type { ContainerListingEntry } from '../shared/types';
import type { SortDirection } from './container-listing';
import type { ContainerType } from './storage-api';

/** The serializable list state restored by a Batches detail-page return. */
export interface BatchBrowseSnapshot {
  entries: ContainerListingEntry[];
  nextToken?: string;
  query: string;
  sortDir: SortDirection;
  attentionFirst: boolean;
  incompleteOnly: boolean;
  resultLimit: BatchResultLimit;
  scrollY: number;
}

export const BATCH_RESULT_LIMITS = [5, 10, 25, 50] as const;
export type BatchResultLimit = (typeof BATCH_RESULT_LIMITS)[number];
export const LEGACY_BATCH_RESULT_LIMIT: BatchResultLimit = 25;

function isBatchResultLimit(value: unknown): value is BatchResultLimit {
  return typeof value === 'number' && BATCH_RESULT_LIMITS.includes(value as BatchResultLimit);
}

export interface BrowseReturnFlash {
  kind: 'success';
  message: string;
}

/** Router-owned context passed from Batches through detail and SKU routes. */
export interface BrowseReturnContext {
  batchBrowseSnapshot: BatchBrowseSnapshot;
  flash?: BrowseReturnFlash;
}

const ENTITY_TYPES = new Set(['RACK', 'SHELF', 'BOX', 'ROW', 'BATCH']);

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isJsonValue(value: unknown, seen = new WeakSet<object>()): boolean {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every((item) => isJsonValue(item, seen));
  if (!isPlainRecord(value) || seen.has(value)) return false;
  seen.add(value);
  return Object.values(value).every((item) => isJsonValue(item, seen));
}

function isContainerListingEntry(value: unknown): value is ContainerListingEntry {
  if (!isPlainRecord(value)) return false;
  return (
    typeof value.entityId === 'string' &&
    ENTITY_TYPES.has(value.entityType as string) &&
    isOptionalString(value.rackCode) &&
    isOptionalString(value.shelfCode) &&
    isOptionalString(value.rowCode) &&
    isOptionalString(value.sourceRunId) &&
    (value.batchStatus === undefined || isPlainRecord(value.batchStatus)) &&
    (value.cardPreviews === undefined || Array.isArray(value.cardPreviews)) &&
    isJsonValue(value)
  );
}

function batchBrowseSnapshotFrom(value: unknown): BatchBrowseSnapshot | undefined {
  if (!isPlainRecord(value) || !Array.isArray(value.entries)) return undefined;
  if (
    !value.entries.every(isContainerListingEntry) ||
    !isOptionalString(value.nextToken) ||
    typeof value.query !== 'string' ||
    (value.sortDir !== 'asc' && value.sortDir !== 'desc') ||
    typeof value.attentionFirst !== 'boolean' ||
    typeof value.incompleteOnly !== 'boolean' ||
    (value.resultLimit !== undefined && !isBatchResultLimit(value.resultLimit)) ||
    typeof value.scrollY !== 'number' ||
    !Number.isFinite(value.scrollY)
  ) {
    return undefined;
  }
  return {
    entries: value.entries,
    ...(value.nextToken !== undefined ? { nextToken: value.nextToken as string } : {}),
    query: value.query,
    sortDir: value.sortDir,
    attentionFirst: value.attentionFirst,
    incompleteOnly: value.incompleteOnly,
    resultLimit:
      value.resultLimit === undefined
        ? LEGACY_BATCH_RESULT_LIMIT
        : (value.resultLimit as BatchResultLimit),
    scrollY: value.scrollY,
  };
}

function browseReturnFlashFrom(value: unknown): BrowseReturnFlash | undefined {
  if (!isPlainRecord(value) || value.kind !== 'success' || typeof value.message !== 'string') {
    return undefined;
  }
  return value.message === '' ? undefined : { kind: 'success', message: value.message };
}

/** Construct router-safe browse context from the current Batches view. */
export function batchBrowseReturnContext(
  snapshot: BatchBrowseSnapshot,
): BrowseReturnContext | undefined {
  return browseReturnContextFrom({ batchBrowseSnapshot: snapshot });
}

/** Add the one-time detail flash without discarding a valid browse snapshot. */
export function withBrowseReturnFlash(
  context: BrowseReturnContext | undefined,
  flash: BrowseReturnFlash,
): BrowseReturnContext | { flash: BrowseReturnFlash } {
  return context === undefined ? { flash } : { ...context, flash };
}

/** Consume a one-time flash while retaining the durable browse snapshot. */
export function withoutBrowseReturnFlash(
  context: BrowseReturnContext | undefined,
): BrowseReturnContext | undefined {
  return context === undefined ? undefined : { batchBrowseSnapshot: context.batchBrowseSnapshot };
}

/** Parse untrusted router state. Invalid or partial values have no browse context. */
export function browseReturnContextFrom(state: unknown): BrowseReturnContext | undefined {
  if (!isPlainRecord(state)) return undefined;
  const snapshot = batchBrowseSnapshotFrom(state.batchBrowseSnapshot);
  if (snapshot === undefined) return undefined;
  const flash = state.flash === undefined ? undefined : browseReturnFlashFrom(state.flash);
  if (state.flash !== undefined && flash === undefined) return undefined;
  return { batchBrowseSnapshot: snapshot, ...(flash !== undefined ? { flash } : {}) };
}

/** Storage_Batches are individually labeled; other container lists support sheets. */
export function supportsBulkLabelPrinting(type: ContainerType): boolean {
  return type !== 'batches';
}
